import {
    CheckCircle,
    ContentCopy,
    DataObject,
    ErrorOutline,
    FormatListBulleted,
    Terminal,
} from '@mui/icons-material'
import {
    Box,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material'
import { type FC, type ReactElement, useState } from 'react'

interface DevAffordancesProps {
    conversationId: string
    rawJsonMode: boolean
    onToggleRawJson: (next: boolean) => void
}

type CopyStatus = 'copied' | 'error'
type CopyState = { key: string; state: CopyStatus } | null

const useCopier = () => {
    const [status, setStatus] = useState<{
        key: string
        state: CopyStatus
    } | null>(null)
    const copy = (key: string, value: string) => {
        navigator.clipboard
            .writeText(value)
            .then(() => {
                setStatus({ key, state: 'copied' })
                setTimeout(
                    () =>
                        setStatus((prev) => (prev?.key === key ? null : prev)),
                    1200
                )
            })
            .catch(() => {
                setStatus({ key, state: 'error' })
                setTimeout(
                    () =>
                        setStatus((prev) => (prev?.key === key ? null : prev)),
                    2000
                )
            })
    }
    return { status, copy }
}

const tooltipFor = (
    key: string,
    defaultText: string,
    status: CopyState
): string => {
    if (status?.key !== key) return defaultText
    return status.state === 'copied' ? 'Copied!' : 'Copy failed'
}

const copyIcon = (
    key: string,
    defaultIcon: ReactElement,
    status: CopyState
): ReactElement => {
    if (status?.key !== key) return defaultIcon
    return status.state === 'copied' ? (
        <CheckCircle fontSize="small" color="success" />
    ) : (
        <ErrorOutline fontSize="small" color="error" />
    )
}

export const DevAffordances: FC<DevAffordancesProps> = ({
    conversationId,
    rawJsonMode,
    onToggleRawJson,
}) => {
    const { status, copy } = useCopier()

    // Escape single quotes in case the ID ever contains one (UUIDs won't, but cheap safety).
    const safeId = conversationId.replace(/'/g, "''")
    const sqlSnippet = `SELECT * FROM bot_messages WHERE conversation_id = '${safeId}' ORDER BY created_at;`

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
                px: 2,
                py: 1,
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderColor: 'divider',
                fontSize: '0.7rem',
            }}
            data-testid="bot-audit-dev-affordances"
        >
            <Box
                sx={{
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {conversationId}
            </Box>
            <Tooltip title={tooltipFor('id', 'Copy conversation ID', status)}>
                <IconButton
                    size="small"
                    onClick={() => copy('id', conversationId)}
                    aria-label="Copy conversation ID"
                    data-testid="bot-audit-dev-copy-id"
                >
                    {copyIcon('id', <ContentCopy fontSize="small" />, status)}
                </IconButton>
            </Tooltip>
            <Tooltip
                title={tooltipFor(
                    'sql',
                    'Copy SQL to fetch this conversation',
                    status
                )}
            >
                <IconButton
                    size="small"
                    onClick={() => copy('sql', sqlSnippet)}
                    aria-label="Copy SQL"
                    data-testid="bot-audit-dev-copy-sql"
                >
                    {copyIcon('sql', <Terminal fontSize="small" />, status)}
                </IconButton>
            </Tooltip>
            <ToggleButtonGroup
                size="small"
                value={rawJsonMode ? 'json' : 'timeline'}
                exclusive
                onChange={(_, v) => v && onToggleRawJson(v === 'json')}
                sx={{ ml: 1 }}
                data-testid="bot-audit-dev-view-mode"
            >
                <ToggleButton
                    value="timeline"
                    sx={{ py: 0, px: 1, fontSize: '0.65rem' }}
                    aria-label="Timeline view"
                >
                    <FormatListBulleted sx={{ fontSize: '0.85rem', mr: 0.5 }} />
                    Timeline
                </ToggleButton>
                <ToggleButton
                    value="json"
                    sx={{ py: 0, px: 1, fontSize: '0.65rem' }}
                    aria-label="Raw JSON view"
                >
                    <DataObject sx={{ fontSize: '0.85rem', mr: 0.5 }} />
                    JSON
                </ToggleButton>
            </ToggleButtonGroup>
        </Stack>
    )
}
